<template>
  <div v-if="show" class="modal-wrapper">
    <div class="modal-backdrop" @click="closeModal"></div>
    <div class="modal-dialog" role="dialog">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">{{ translate('upload_interactions.title', 'Upload Interactions') }}</h5>
          <button type="button" class="btn-close" @click="closeModal"></button>
        </div>
        <div class="modal-body">
          <div class="mb-4">
            <p>{{ translate('upload_interactions.file_requirements', 'Upload a CSV file with multiple interactions. The file should contain the following columns:') }}</p>
            <ul class="small">
              <li><strong>phone</strong> - {{ translate('interactions.phone_description', 'Phone number without country code, digits only.') }} {{translate('common.required', '(required)') }}</li>
              <li><strong>customerName</strong> - {{ translate('interactions.customer_name', 'Customer name') }} {{translate('common.required', '(required)') }}</li>
              <li><strong>petName</strong> - {{ translate('interactions.pet_name', 'Pet name') }} {{translate('common.required', '(required)') }}</li>
              <li><strong>service</strong> - {{ translate('interactions.service_type_description', 'Service you want to evaluate (e.g., bath, nail trim, vet consultation).') }} {{translate('common.required', '(required)') }}</li>
              <li><strong>serviceDateTime</strong> - {{ translate('interactions.service_date_time_description', 'Service date and time in YYYY-MM-DD HH:MM format') }} {{translate('common.required', '(required)') }}</li>
            </ul>
          </div>

          <div class="mb-4">
            <button @click="downloadSampleCsv" class="btn btn-outline-primary">
              <i class="bi bi-download me-2"></i>{{ translate('upload_interactions.download_template', 'Download Sample CSV') }}
            </button>
          </div>

          <div class="mb-4">
            <label for="csvFile" class="form-label">{{ translate('upload_interactions.select_file', 'Upload Your CSV File') }}</label>
            <input 
              type="file" 
              id="csvFile" 
              class="form-control" 
              accept=".csv" 
              @change="handleFileSelect"
              ref="fileInput"
            >
          </div>

          <div v-if="validationResults.length > 0 && !importCompleted" class="mb-4">
            <h6 class="fw-bold mb-2">{{ translate('upload_interactions.processing', 'Validation Results') }}</h6>
            
            <!-- Show validation errors -->
            <div v-if="hasErrors" class="alert alert-danger">
              <p class="mb-2">
                <i class="bi bi-exclamation-triangle me-2"></i>
                {{ translate('upload_interactions.upload_error', 'Found') }} {{ errorCount }} {{ translate('messages.errorOccurred', 'errors in the CSV file. Please fix them and try again.') }}
              </p>
              <div class="validation-errors">
                <ul class="mb-0">
                  <li v-for="(result, index) in validationResults" :key="index" class="text-small">
                    <template v-if="result.errors.length > 0">
                      Row {{ result.row }}: {{ result.errors.join(', ') }}
                    </template>
                  </li>
                </ul>
              </div>
            </div>
            
            <!-- Show success or credits warning -->
            <div v-else class="alert" :class="initialCreditsValidation ? 'alert-success' : 'alert-warning'">
              <!-- Success message when enough credits -->
              <p v-if="initialCreditsValidation" class="mb-2">
                <i class="bi bi-check-circle me-2"></i>
                {{ translate('upload_interactions.upload_success', 'CSV file is valid.') }} {{ validInteractions.length }} {{ translate('interactions.title', 'interactions') }} {{ translate('messages.ready', 'ready to import') }}.
              </p>
              
              <!-- Warning message when not enough credits -->
              <div v-else>
                <p class="mb-2">
                  <i class="bi bi-exclamation-triangle me-2"></i>
                  {{ translate('upload_interactions.credits_insufficient', 'Insufficient credits for this upload.') }}
                </p>
                <div class="d-flex justify-content-between align-items-center">
                  <div class="text-start">
                    <small class="text-muted d-block">{{ translate('upload_interactions.required_credits', 'Required credits:') }} {{ validInteractions.length }}</small>
                    <small class="text-muted d-block">{{ translate('upload_interactions.available_credits', 'Available credits:') }} {{ credits }}</small>
                  </div>
                  <div></div>
                </div>
              </div>
            </div>
          </div>

          <div class="text-end">
            <button type="button" class="btn btn-secondary me-2" @click="closeModal">
              {{ translate('messages.cancel', 'Cancel') }}
            </button>
            <!-- Show Import button when there are enough credits -->
            <button 
              v-if="initialCreditsValidation"
              type="button" 
              class="btn btn-primary" 
              @click="submitInteractions" 
              :disabled="loading || validInteractions.length === 0"
            >
              <span v-if="loading" class="spinner-border spinner-border-sm me-2"></span>
              {{ translate('upload_interactions.upload', 'Import') }} {{ validInteractions.length }} {{ translate('interactions.title', 'Interactions') }}
            </button>
            
            <!-- Show Purchase Credits button when there are not enough credits -->
            <button 
              v-else
              type="button" 
              class="btn btn-warning" 
              @click="handlePurchaseCredits"
              :disabled="loading"
            >
              <i class="bi bi-plus-circle me-2"></i>
              {{ translate('payment.buy_credits', 'Purchase Credits') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed } from 'vue'
import { useStore } from 'vuex'

export default {
  name: 'UploadInteractionsModal',
  props: {
    show: {
      type: Boolean,
      default: false
    }
  },
  setup(props, { emit }) {
    const store = useStore()
    const fileInput = ref(null)
    const loading = ref(false)
    const validationResults = ref([])
    const csvData = ref([])
    const importCompleted = ref(false)
    const initialCreditsValidation = ref(true) // Capture initial validation state
    
    // Get credits from store
    const credits = computed(() => store.state.credits || 0)

    // Translation function to use the centralized system from Vuex
    const translate = (key, fallback) => {
      return store.getters.translate(key, fallback)
    }

    const hasErrors = computed(() => {
      return validationResults.value.some(result => result.errors.length > 0)
    })

    const errorCount = computed(() => {
      return validationResults.value.reduce((count, result) => {
        return count + result.errors.length
      }, 0)
    })

    const validInteractions = computed(() => {
      return validationResults.value
        .filter(result => result.errors.length === 0)
        .map(result => result.data)
    })
    
    // Check if there are enough credits for the valid interactions
    const hasEnoughCredits = computed(() => {
      return credits.value >= validInteractions.value.length
    })

    const closeModal = () => {
      // Reset form state
      fileInput.value.value = ''
      validationResults.value = []
      csvData.value = []
      importCompleted.value = false
      initialCreditsValidation.value = true
      emit('update:show', false)
    }
    
    const handlePurchaseCredits = () => {
      // Close the upload modal and emit event to show purchase credits modal
      closeModal()
      emit('purchase-credits')
    }

    const downloadSampleCsv = () => {
      // Create sample data
      const sampleData = [
        ['phone', 'customerName', 'petName', 'service', 'serviceDateTime'],
        ['1234567890', 'John Smith', 'Buddy', 'Grooming', '2025-02-26 14:30'],
        ['9876543210', 'Jane Doe', 'Max', 'Retail', '2025-02-27 10:00'],
        ['5555555555', 'Bob Johnson', 'Luna', 'Grooming', '2025-02-28 09:15']
      ]

      // Convert to CSV string
      const csvContent = sampleData.map(row => row.join(',')).join('\n')
      
      // Create a blob and download link
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', 'paw_pulse_interactions_template.csv')
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    }

    const handleFileSelect = (event) => {
      const file = event.target.files[0]
      if (!file) return

      const reader = new FileReader()
      reader.onload = (e) => {
        const content = e.target.result
        parseCSV(content)
      }
      reader.readAsText(file)
    }

    const parseCSV = (content) => {
      // Basic CSV parsing (could use a library like Papa Parse for more robust handling)
      const lines = content.split('\n')
      const headers = lines[0].split(',').map(h => h.trim())
      
      // Check required headers
      const requiredHeaders = ['phone', 'customerName', 'service', 'serviceDateTime']
      const missingHeaders = requiredHeaders.filter(h => !headers.includes(h))
      
      if (missingHeaders.length > 0) {
        validationResults.value = [{
          row: 0,
          errors: [`Missing required headers: ${missingHeaders.join(', ')}`],
          data: null
        }]
        return
      }

      // Parse data rows
      const results = []
      for (let i = 1; i < lines.length; i++) {
        const line = lines[i].trim()
        if (!line) continue // Skip empty lines
        
        const values = line.split(',').map(v => v.trim())
        const rowData = {}
        const rowErrors = []
        
        // Map values to object using headers
        headers.forEach((header, index) => {
          if (index < values.length) {
            rowData[header] = values[index]
          } else {
            rowData[header] = ''
          }
        })
        
        // Validate required fields
        if (!rowData.phone) rowErrors.push('Phone number is required')
        if (!rowData.customerName) rowErrors.push('Customer name is required')
        if (!rowData.service) rowErrors.push('Service is required')
        if (!rowData.serviceDateTime) rowErrors.push('Service date and time is required')
        
        // Validate service type
        if (rowData.service && !['Grooming', 'Retail'].includes(rowData.service)) {
          rowErrors.push('Service must be either "Grooming" or "Retail"')
        }
        
        // Validate date format
        if (rowData.serviceDateTime) {
          const dateRegex = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}$/
          if (!dateRegex.test(rowData.serviceDateTime)) {
            rowErrors.push('Service date time must be in format YYYY-MM-DD HH:MM')
          }
        }
        
        // Convert date format for Firebase
        if (rowData.serviceDateTime && !rowErrors.some(e => e.includes('Service date time'))) {
          // Convert the date string to a proper ISO string for Firebase
          const [datePart, timePart] = rowData.serviceDateTime.split(' ')
          rowData.date = new Date(`${datePart}T${timePart}`).toISOString()
        }
        
        results.push({
          row: i,
          errors: rowErrors,
          data: rowData
        })
      }
      
      validationResults.value = results
      csvData.value = results.map(r => r.data)
      
      // Capture initial credits validation state (before any processing)
      const validInteractionsCount = results.filter(r => r.errors.length === 0).length
      initialCreditsValidation.value = credits.value >= validInteractionsCount
    }

    const submitInteractions = async () => {
      if (hasErrors.value || validInteractions.value.length === 0) return
      
        loading.value = true
      
      try {
        // Process each valid interaction
        for (const interaction of validInteractions.value) {
          await store.dispatch('createInteraction', {
            customerName: interaction.customerName,
            petName: interaction.petName || '',
            phone: interaction.phone,
            service: interaction.service,
            date: interaction.date,
            createdAt: new Date().toISOString()
          })
        }
        
        // Mark import as completed
        importCompleted.value = true
        
        // Show success message
        const successMessage = translate('upload_interactions.success', 'Successfully imported {count} interactions.')
        alert(successMessage.replace('{count}', validInteractions.value.length))
        
        // Refresh interactions list
        await store.dispatch('fetchInteractions')
        await store.dispatch('fetchCustomers')
        
        // Close modal after successful import
        closeModal()
      } catch (error) {
        console.error('Error importing interactions:', error)
        alert(`Error importing interactions: ${error.message}`)
      } finally {
        loading.value = false
      }
    }

    return {
      fileInput,
      loading,
      validationResults,
      hasErrors,
      errorCount,
      validInteractions,
      credits,
      hasEnoughCredits,
      importCompleted,
      initialCreditsValidation,
      closeModal,
      handlePurchaseCredits,
      downloadSampleCsv,
      handleFileSelect,
      submitInteractions,
      translate
    }
  }
}
</script>

<style scoped>
.modal-wrapper {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1050;
}

.modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1040;
}

.modal-dialog {
  position: relative;
  z-index: 1060;
  width: 100%;
  max-width: 600px;
  margin: 1.75rem auto;
  pointer-events: none;
}

.modal-content {
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  pointer-events: auto;
  background-color: #fff;
  background-clip: padding-box;
  border: 1px solid rgba(0, 0, 0, 0.2);
  border-radius: 0.3rem;
  outline: 0;
  max-height: 90vh;
  overflow-y: auto;
}

.modal-header {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1rem;
  border-bottom: 1px solid #dee2e6;
  border-top-left-radius: calc(0.3rem - 1px);
  border-top-right-radius: calc(0.3rem - 1px);
}

.modal-body {
  position: relative;
  flex: 1 1 auto;
  padding: 1rem;
}

.validation-errors {
  max-height: 150px;
  overflow-y: auto;
  font-size: 0.875rem;
}

p {
  font-size: 1rem; 
}
</style>
